import { useQuery } from "@tanstack/react-query";
import {
  DollarSign,
  ShoppingBag,
  Package,
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Eye,
  Download,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import adminService, { DashboardMetrics } from "@/services/adminService";
import { formatCurrency } from "@/lib/utils";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  growth?: number;
  description?: string;
  color?: "blue" | "green" | "yellow" | "red" | "purple";
}

const MetricCard = ({
  title,
  value,
  icon: Icon,
  growth,
  description,
  color = "blue",
}: MetricCardProps) => {
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    red: "bg-red-500",
    purple: "bg-purple-500",
  };

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-900">
          {typeof value === "number" && title.includes("$")
            ? formatCurrency(value)
            : value}
        </div>

        {growth !== undefined && (
          <div className="flex items-center mt-1">
            {growth >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
            )}
            <span
              className={`text-sm font-medium ${
                growth >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {growth >= 0 ? "+" : ""}
              {growth}%
            </span>
          </div>
        )}

        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};

const RecentOrdersTable = () => {
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ["admin-recent-orders"],
    queryFn: () => adminService.getAllOrders({ limit: 5, page: 1 }),
    staleTime: 2 * 60 * 1000, // 2 minutos
  });

  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Órdenes Recientes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-3">
                <Skeleton className="h-10 w-20" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Órdenes Recientes</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/admin/orders")}
        >
          Ver Todas
        </Button>
      </CardHeader>
      <CardContent>
        {ordersData?.orders.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            No hay órdenes recientes
          </div>
        ) : (
          <div className="space-y-3">
            {ordersData?.orders.map((order) => (
              <div
                key={order._id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div>
                    <p className="font-medium text-sm">#{order.orderNumber}</p>
                    <p className="text-xs text-muted-foreground">
                      {order.user?.name}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="font-medium text-sm">
                      {formatCurrency(order.total)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {order.totalItems}{" "}
                      {order.totalItems === 1 ? "artículo" : "artículos"}
                    </p>
                  </div>

                  <Badge className={adminService.getStatusColor(order.status)}>
                    {adminService.getStatusLabel(order.status)}
                  </Badge>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/admin/orders`)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const QuickActions = () => {
  const navigate = useNavigate();

  const actions = [
    {
      title: "Crear Producto",
      description: "Agregar nuevo producto al catálogo",
      icon: Package,
      onClick: () => navigate("/admin/products"),
      color: "blue",
    },
    {
      title: "Ver Órdenes",
      description: "Gestionar pedidos pendientes",
      icon: ShoppingBag,
      onClick: () => navigate("/admin/orders"),
      color: "green",
    },
    {
      title: "Reportes",
      description: "Análisis de ventas y métricas",
      icon: Download,
      onClick: () => navigate("/admin/reports"),
      color: "purple",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Acciones Rápidas</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {actions.map((action) => (
            <button
              key={action.title}
              onClick={action.onClick}
              className="w-full rounded-md border border-input bg-background p-4 flex flex-col items-center gap-2 hover:shadow-md hover:bg-accent transition-all text-center"
            >
              <action.icon className="h-6 w-6 text-primary shrink-0" />
              <div className="w-full">
                <div className="font-medium text-sm">{action.title}</div>
                <div className="text-xs text-muted-foreground whitespace-normal break-words">
                  {action.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const AlertsAndNotifications = ({
  metrics,
}: {
  metrics?: DashboardMetrics;
}) => {
  const alerts = [];

  if (metrics?.products.lowStock > 0) {
    alerts.push({
      type: "warning",
      title: "Productos con bajo stock",
      message: `${metrics.products.lowStock} productos necesitan reposición`,
      action: "Ver Productos",
    });
  }

  if (metrics?.orders.pending > 10) {
    alerts.push({
      type: "info",
      title: "Órdenes pendientes",
      message: `${metrics.orders.pending} órdenes esperan procesamiento`,
      action: "Ver Órdenes",
    });
  }

  if (alerts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notificaciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            Todo está funcionando correctamente
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <span>Notificaciones</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {alerts.map((alert, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 rounded-lg border border-yellow-200 bg-yellow-50"
            >
              <div>
                <p className="font-medium text-sm text-yellow-800">
                  {alert.title}
                </p>
                <p className="text-xs text-yellow-600">{alert.message}</p>
              </div>
              <Button variant="outline" size="sm">
                {alert.action}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const { toast } = useToast();

  const {
    data: metrics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-dashboard-metrics"],
    queryFn: () => adminService.getDashboardMetrics(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  if (error) {
    toast({
      title: "Error",
      description: "No se pudieron cargar las métricas del dashboard",
      variant: "destructive",
    });
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-muted-foreground">
          Resumen general de tu tienda en línea
        </p>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Ventas Totales"
          value={metrics?.sales.total || 0}
          icon={DollarSign}
          growth={metrics?.sales.growth}
          description="Ingresos totales"
          color="green"
        />

        <MetricCard
          title="Órdenes Hoy"
          value={metrics?.orders.today || 0}
          icon={ShoppingBag}
          description={`${metrics?.orders.pending || 0} pendientes`}
          color="blue"
        />

        <MetricCard
          title="Productos"
          value={metrics?.products.active || 0}
          icon={Package}
          description={`${metrics?.products.total || 0} total`}
          color="purple"
        />

        <MetricCard
          title="Usuarios"
          value={metrics?.users.total || 0}
          icon={Users}
          description={`${metrics?.users.newThisMonth || 0} nuevos este mes`}
          color="yellow"
        />
      </div>

      {/* Segunda fila de contenido */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentOrdersTable />
        <AlertsAndNotifications metrics={metrics} />
      </div>

      {/* Tercera fila */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuickActions />

        {/* Espacio para futuros componentes como gráficos */}
        <Card>
          <CardHeader>
            <CardTitle>Ventas por Período</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              Gráfico de ventas próximamente
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
